import { Asset, Carousel, LinkCard, ProgressBar } from '@/components';
import { useSparkColor } from '@/hooks';
import {
  ASSET_SYMBOLS_PALETTE,
  Box,
  CurrencyValue,
  Flex,
  FlexProps,
  NetworkIcon,
  PercentageValue,
  ProtocolIcon,
  RelativeDateTime,
  Text
} from '@blockanalitica/ui';
import styled, { css } from 'styled-components';

export type TokenData = {
  address: string;
  datetime: string;
  wallet_address: string;
  order_index: string;
  assets: string;
  assets_change: string;
  assets_usd: string;
  assets_usd_change: string;
  symbol: string;
  name: string;
  network: string;
  protocol: string;
  apy: string;
  apy_change: string;
  estimated_revenue_usd: string;
  asset_address: string;
  asset_symbol: string;
  asset_name: string;
  aum: number;
  aum_change: number;
};

export interface TokenCarouselProps {
  data: TokenData[];
  totalAUM: number;
}

const KpiTitle = styled(Flex)<FlexProps>`
  ${(props) => css`
    color: ${props.theme.colors.muted};
    font-size: ${props.theme.sizes['-1']};
  `}
`;

const KpiValue = styled(Flex)<FlexProps>`
  font-weight: 500;

  ${(props) => css`
    font-size: ${props.theme.sizes['1']};
  `}
`;

export default function TokenCarousel({
  data,
  totalAUM,
  ...rest
}: TokenCarouselProps) {
  const color = useSparkColor();
  return (
    <Carousel {...rest}>
      {data.map((row) => (
        <LinkCard
          to={`/spark-liquidity-layer/${row.network}/${row.wallet_address}/${row.address}`}
          key={row.address}
          gap="m"
          justifyContent="space-between"
          flexDirection="column"
          hoverBorderColor={color}>
          <Flex flexDirection="column" gap="m">
            <Flex justifyContent="space-between">
              <Asset
                symbol={row.asset_symbol}
                address={row.asset_address}
                network={row.network}
                name={row.asset_name}
                iconSize="5"
              />
              <Flex justifyContent="flex-start" gap="2xs">
                <Flex gap="2xs" alignItems="center">
                  <NetworkIcon network={row.network} size="2" />
                </Flex>
                <Flex>
                  {row.protocol ? (
                    <Flex gap="2xs" alignItems="center">
                      <ProtocolIcon protocol={row.protocol} size="2" />
                    </Flex>
                  ) : null}
                </Flex>
              </Flex>
            </Flex>
            <Flex gap="xs" justifyContent="space-between">
              <Flex flexDirection="column" flex="1" gap="3xs">
                <KpiTitle>Allocated Value</KpiTitle>
                <KpiValue>
                  <CurrencyValue
                    value={row.aum}
                    fontSize="4"
                    variant="dynamic"
                  />
                </KpiValue>
              </Flex>
              <Flex
                flexDirection="column"
                flex="1"
                gap="3xs"
                alignItems="flex-end">
                <KpiTitle color="muted" fontSize="-1">
                  APY
                </KpiTitle>
                <KpiValue>
                  <PercentageValue
                    value={row.apy}
                    fontSize="4"
                    variant="dynamic"
                  />
                </KpiValue>
              </Flex>
            </Flex>
          </Flex>
          <ProgressBar
            color={ASSET_SYMBOLS_PALETTE[row.asset_symbol] || '#FFFFFF'}
            percent={Number(row.assets) / totalAUM}
            text={
              <Text color="muted" size="2">
                % of total allocated assets
              </Text>
            }
            height="-4"
            muted={true}
          />

          <Flex justifyContent="space-between">
            <Flex color="muted" gap="3xs" fontSize="-1">
              Last activity <RelativeDateTime value={row.datetime} />
            </Flex>

            <Box fontSize="-1" color="link">
              View details
            </Box>
          </Flex>
        </LinkCard>
      ))}
    </Carousel>
  );
}
